<script setup lang="ts">
  import { defaultWindow, useInfiniteScroll } from '@vueuse/core';
  import { computed, ref, watch } from 'vue';
  import SerpCard from './SerpCard.vue';
  import SerpCardSkeleton from './SerpCardSkeleton.vue';
  import { SERP_LIST_LOAD_DISTANCE, SERP_LIST_PAGE_SIZE } from '../config/list';
  import type { FiltersModel, SerpCardOpenOptions, SerpProduct } from '../model/types';

  interface SerpListProps {
    products: SerpProduct[];
    loading?: boolean;
    skeletonCount?: number;
    pageSize?: number;
    filtersModel: FiltersModel;
  }

  const props = withDefaults(defineProps<SerpListProps>(), {
    loading: false,
    skeletonCount: 3,
    pageSize: SERP_LIST_PAGE_SIZE,
  });

  interface SerpListEmits {
    (e: 'open', product: SerpProduct, options: SerpCardOpenOptions): void;
    (e: 'visible', count: number): void;
    (e: 'view-card', position: number): void;
  }

  const emit = defineEmits<SerpListEmits>();

  // Выдача — сотни отелей, а карточка тянет за собой галерею на десяток фото, поэтому
  // в DOM держим только отрисованный кусок и наращиваем его по мере скролла.
  const visibleCount = ref(props.pageSize);

  const visibleProducts = computed<SerpProduct[]>(() => props.products.slice(0, visibleCount.value));
  const hasMore = computed<boolean>(() => visibleCount.value < props.products.length);
  const renderedCount = computed<number>(() => (props.loading ? 0 : visibleProducts.value.length));

  watch(renderedCount, (count) => emit('visible', count), { immediate: true });

  const rootRef = ref<HTMLElement | null>(null);

  const isListShown = (): boolean => {
    const root = rootRef.value;
    return !!root && root.getClientRects().length > 0 && getComputedStyle(root).visibility !== 'hidden';
  };

  useInfiniteScroll(
    defaultWindow,
    () => {
      visibleCount.value = Math.min(visibleCount.value + props.pageSize, props.products.length);
    },
    {
      distance: SERP_LIST_LOAD_DISTANCE,
      canLoadMore: () => hasMore.value && isListShown(),
    },
  );

  // Новый поиск чистит выдачу — значит показываем её снова с первой страницы. На порции
  // одного поиска не реагируем: массив только растёт, а пользователь уже мог долистать.
  watch(
    () => props.products.length,
    (length) => {
      if (length === 0) visibleCount.value = props.pageSize;
    },
  );
</script>

<template>
  <div
    ref="rootRef"
    class="serp-list"
    data-testid="serp-list"
  >
    <template v-if="loading">
      <SerpCardSkeleton
        v-for="index in skeletonCount"
        :key="index"
        class="serp-list__item"
      />
    </template>

    <template v-else>
      <div
        v-for="(product, index) in visibleProducts"
        :key="`${product.hotel.id}_${product.cheapProduct?.id}`"
      >
        <SerpCard
          class="serp-list__item"
          :product="product"
          :is-visible-prepayment="!filtersModel.prepayments.length"
          @open="emit('open', product, $event)"
          @view="emit('view-card', index + 1)"
        />

        <slot
          name="banner"
          :product="product.products[0]"
          :card-position="index + 1"
          :total-cards="products.length"
        />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .serp-list {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);
    padding: 0 var(--spacing-xs);
    width: 100%;
  }
</style>
